import { PoolOptions } from "@quint/components";
import { truncateEthAddress } from "@quint/services/web3";
import { Button, Chip, Image, type ButtonProps } from "@quint/ui";
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from "@quint/ui/tooltip";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { Download } from "lucide-react";
import { type StaticImageData } from "next/image";
import { isValidElement, useCallback, type MouseEvent, type ReactElement } from "react";
import { useAccount, type Address } from "wagmi";

export type AwardCardProps = {
  status: "done" | "inProgress" | "comingSoon";

  drawDate: string;
  poolName: string;
  stakedAmount: ReactElement;
  imageSrc: string | StaticImageData;
  imageAlt: string;
  name: string;
  winningWallets?: string[] | undefined;
  address: Address;
  withdrawButton: ReactElement;
};

const statusDisplayMap = {
  done: "Finished",
  inProgress: "In Progress",
  comingSoon: "Coming Soon",
};

const WithdrawButton = (props: ButtonProps) => {
  const { onClick, ...rest } = props;
  const { isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (!isConnected) {
        openConnectModal?.();
      } else {
        onClick?.(e);
      }
    },
    [isConnected, onClick, openConnectModal]
  );
  return (
    <Button variant="primary" className="grow gap-4" startIcon={<Download />} onClick={handleClick} {...rest}>
      Withdraw
    </Button>
  );
};

const Card = ({
  status,
  withdrawButton,
  drawDate,
  poolName,
  stakedAmount,
  imageAlt,
  imageSrc,
  name,
  winningWallets,
  address,
}: AwardCardProps) => {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-[#E6E8EA] bg-white dark:border-[#354856] dark:bg-[#01111D]">
      <Image
        src={imageSrc}
        alt={imageAlt}
        className="aspect-square w-full object-cover md:aspect-[1.2]"
        sizes="(max-width: 768px) 90vw,
        (max-width: 1280px) 48vw,
        30vw"
        quality={100}
        // storybook specific
        {...(typeof imageSrc === "string" && { width: 100, height: 100 })}
      />

      <div className="flex flex-1 flex-col p-6">
        <p className="text-[18px] font-semibold md:h-[54px]">{name}</p>
        <Chip variant={status} className="mt-2">
          {poolName} {statusDisplayMap[status]}
        </Chip>
        <div className="mt-7 grid gap-2">
          <div className="flex items-center justify-between gap-1 md:h-[46px]">
            <p className="text-[14px] text-slate-700 dark:text-[#FFFFFFB2]/70">WINNING WALLET</p>
            <div className="flex flex-col gap-1">
              {winningWallets?.map((winningWallet) => (
                <TooltipProvider key={winningWallet} delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-[14px] font-semibold">{truncateEthAddress(winningWallet) ?? "-"}</p>
                    </TooltipTrigger>
                    <TooltipContent align="end">
                      <p className="text-[14px] font-semibold">{winningWallet ?? "-"}</p>
                      <TooltipArrow width={11} height={5} />
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          </div>
          <div className="flex justify-between">
            <p className="text-[14px] text-slate-700 dark:text-[#FFFFFFB2]/70">DRAW DATE</p>
            <p className="text-[14px] font-semibold">{drawDate}</p>
          </div>
          <div className="flex justify-between">
            <p className="text-[14px] text-slate-700 dark:text-[#FFFFFFB2]/70">YOU HAVE STAKED</p>
            <p className="text-[14px] font-semibold text-[#0B6047] dark:text-[#1FB787]">
              {isValidElement(stakedAmount) && stakedAmount}
            </p>
          </div>
        </div>

        <div className="mt-6 flex gap-4">
          {isValidElement(withdrawButton) && withdrawButton}
          <PoolOptions address={address} disabled={status === "comingSoon"} />
        </div>
      </div>
    </div>
  );
};

const Award = { WithdrawButton, Card };
export default Award;
